export type MessageListener = (data: unknown) => void;

// In order to support autocompletion we need to maintain a central repository of message types.
// internal-components is the most internal package that's a common ancestor, being able to share the definition.
export const MESSAGES = {
  PAYMENT_ELEMENT_LOADED: 'flywire-sdk.elements.payment.loaded',
  INJECT_PAYMENT_ELEMENT_APPEARANCE_SETTINGS:
    'flywire-sdk.elements.payment.inject_appearance_settings',
  DOCUMENT_COLLECTOR_ELEMENT_LOADED: 'flywire-sdk.elements.document-collector.loaded',
  INJECT_DOCUMENT_COLLECTOR_APPEARANCE_SETTINGS:
    'flywire-sdk.elements.document-collector.inject_appearance_settings',
  LRS_ELEMENT_LOADED: 'flywire-sdk.elements.lrs.loaded',
  INJECT_LRS_APPEARANCE_SETTINGS: 'flywire-sdk.elements.lrs.inject_appearance_settings',
  PAYMENT_METHOD_SELECTOR_ELEMENT_LOADED: 'flywire-sdk.elements.payment-method-selector.loaded',
  INJECT_PAYMENT_METHOD_SELECTOR_APPEARANCE_SETTINGS:
    'flywire-sdk.elements.payment-method-selector.inject_appearance_settings',
  PAYMENT_METHOD_SELECTOR_ELEMENT_SELECTED: 'flywire-sdk.elements.payment-method-selector.selected',
  RECIPIENT_FIELDS_ELEMENT_LOADED: 'flywire-sdk.elements.recipient-fields.loaded',
  INJECT_RECIPIENT_FIELDS_APPEARANCE_SETTINGS:
    'flywire-sdk.elements.recipient-fields.inject_appearance_settings',
  RECIPIENT_FIELDS_ELEMENT_CHANGED: 'flywire-sdk.elements.recipient-fields.changed'
} as const;

export type MessagesKeys = (typeof MESSAGES)[keyof typeof MESSAGES];

export type Appearance = {
  fonts: AppearanceFontConfig[];
  variables: AppearanceVariables;
};
export type AppearanceVariablesKeys = 'primaryColor' | 'headerFontSize';
export type AppearanceFontConfig = { fontFamily: string; url: string };
export type AppearanceVariables = Partial<Record<AppearanceVariablesKeys, string>>;
export type PaymentElementLoadedMessage = {
  messageName: (typeof MESSAGES)['PAYMENT_ELEMENT_LOADED'];
  payload: object;
};
export type InjectPaymentElementAppearanceSettings = {
  messageName: (typeof MESSAGES)['INJECT_PAYMENT_ELEMENT_APPEARANCE_SETTINGS'];
  payload: {
    appearance: Appearance;
  };
};
export type DocumentCollectorElementLoadedMessage = {
  messageName: (typeof MESSAGES)['DOCUMENT_COLLECTOR_ELEMENT_LOADED'];
  payload: object;
};
export type InjectDocumentCollectorAppearanceSettings = {
  messageName: (typeof MESSAGES)['INJECT_DOCUMENT_COLLECTOR_APPEARANCE_SETTINGS'];
  payload: {
    appearance: Appearance;
  };
};

export type LRSElementLoadedMessage = {
  messageName: (typeof MESSAGES)['LRS_ELEMENT_LOADED'];
  payload: object;
};
export type InjectLRSAppearanceSettings = {
  messageName: (typeof MESSAGES)['INJECT_LRS_APPEARANCE_SETTINGS'];
  payload: {
    appearance: Appearance;
  };
};

export type PaymentMethodSelectorElementLoadedMessage = {
  messageName: (typeof MESSAGES)['PAYMENT_METHOD_SELECTOR_ELEMENT_LOADED'];
  payload: object;
};
export type InjectPaymentMethodSelectorAppearanceSettings = {
  messageName: (typeof MESSAGES)['INJECT_PAYMENT_METHOD_SELECTOR_APPEARANCE_SETTINGS'];
  payload: { appearance: Appearance };
};
export type PaymentMethodSelectorElementSelectedMessage = {
  messageName: (typeof MESSAGES)['PAYMENT_METHOD_SELECTOR_ELEMENT_SELECTED'];
  payload: { schema: string };
};

export type RecipientFieldsElementLoadedMessage = {
  messageName: (typeof MESSAGES)['RECIPIENT_FIELDS_ELEMENT_LOADED'];
  payload: object;
};
export type InjectRecipientFieldsAppearanceSettings = {
  messageName: (typeof MESSAGES)['INJECT_RECIPIENT_FIELDS_APPEARANCE_SETTINGS'];
  payload: { appearance: Appearance };
};
export type RecipientFieldsElementChangedMessage = {
  messageName: (typeof MESSAGES)['RECIPIENT_FIELDS_ELEMENT_CHANGED'];
  payload: { fields: Array<{ id: string; value: string | boolean | string[] }> };
};

export type IframeMessageType =
  | PaymentElementLoadedMessage
  | InjectPaymentElementAppearanceSettings
  | DocumentCollectorElementLoadedMessage
  | InjectDocumentCollectorAppearanceSettings
  | LRSElementLoadedMessage
  | InjectLRSAppearanceSettings
  | PaymentMethodSelectorElementLoadedMessage
  | InjectPaymentMethodSelectorAppearanceSettings
  | PaymentMethodSelectorElementSelectedMessage
  | RecipientFieldsElementLoadedMessage
  | InjectRecipientFieldsAppearanceSettings
  | RecipientFieldsElementChangedMessage;
